<script setup lang="ts">
import type { ZodSchema } from 'zod'
import { computed, useAttrs } from "vue";
import { toTypedSchema } from '@vee-validate/zod'
import { Field, type FieldContext } from "vee-validate";
import { withoutKeys } from '@/helpers'

// Prevent attribute inheritance.
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    name: string
    value?: string
    rules?: ZodSchema,
    fixed?: boolean,
  }>(),
  { value: '', type: 'text' }
)

const $attrs = useAttrs()
const emit = defineEmits<{ 'update:value': [string] }>()
const typedRules = computed(() => (props.rules ? toTypedSchema(props.rules) : undefined))

const isRequired = computed(
  () => $attrs.required === '' || $attrs.required === true
)

async function handleInput($e: Event, meta: FieldContext['meta'], handleChange: FieldContext['handleChange'], validate: FieldContext['validate']) {
  if (meta.touched || meta.validated) {
    await validate()
  } else if (meta.dirty) {
    await validate({ mode: 'silent' })
  }

  if (meta.valid && $e.target instanceof HTMLTextAreaElement) {
    emit('update:value', $e.target.value)
  }
}
</script>

<template>
  <Field
    v-slot="{ field, errorMessage, meta, handleChange, validate }"
    :name="name"
    :rules="typedRules"
    :model-value="value">
    <div
      class="input-group position-relative"
      :class="[
        { 'has-validation': errorMessage, [$style['fixed']]: fixed, [$style['required']]: isRequired },
        $style['input-textarea'],
        $attrs.class
      ]">
      <textarea
        v-bind="{ ...withoutKeys($attrs, ['class']), ...field }"
        class="form-control"
        :class="{ 'is-invalid': errorMessage }"
        @input="handleInput($event, meta, handleChange, validate)" />
      <div class="invalid-tooltip" v-if="errorMessage">{{ errorMessage }}</div>
    </div>
  </Field>
</template>

<style module lang="scss">
@use "@/assets/styles/mixins-inputs";

@import "@/assets/variables";
@import "@/assets/bootstrap-variables";

.input-textarea {
  @include mixins-inputs.fix-error-tooltips-for-inputs;

  :global {
    textarea {
      line-height: 1.5;
    }

    .form-control {
      border-top-right-radius: $input-border-radius !important;
      border-bottom-right-radius: $input-border-radius !important;
    }
  }
}

.fixed {
  textarea {
    resize: none;
  }
}

.required::after {
    @include mixins-inputs.required-indicator-styles;
}
</style>